<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { useODataSource, useDefaultGridOptions } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useChips } from '@/composables/chips'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'

const props = defineProps({
  group: Object,
})

const i18n = getI18n()
const { getServiceName } = usePlanningStore()
const { getServiceStructureTypeChip } = useChips()

const componentLinks = computed(() => props.group.components || [])
const quantityMap = computed(() => Object.fromEntries(componentLinks.value.map(c => [c.id, c.quantity])))

const buildIdFilter = () => {
  const ids = componentLinks.value.map(c => c.id)
  if (ids.length === 0) return ['id', '=', -1]
  const filter = []
  ids.forEach((id, index) => {
    if (index > 0) filter.push('or')
    filter.push(['id', '=', id])
  })
  return filter
}

const gridDataSource = componentLinks.value.length > 0
  ? useODataSource(planningApi.getAbsoluteUrl() + planningApi.servicesUrl, {
    filter: buildIdFilter(),
    map: (item) => ({ ...item, quantity: quantityMap.value[item.id] ?? 1 }),
  })
  : []

const columns = [
  { dataField: 'id', caption: i18n.t('common.id'), width: '3rem' },
  dataGridColumn.useAvatarColumn(),
  { caption: i18n.t('common.name'), width: '14rem', sortOrder: 'asc', calculateCellValue: (row) => getServiceName(row) },
  dataGridColumn.useStructureTypeColumn({ width: '10rem' }),
  { dataField: 'description', caption: i18n.t('common.description'), width: '16rem' },
  { dataField: 'quantity', caption: i18n.t('common.quantity'), width: '6rem', dataType: 'number' },
]

const gridOptions = useDefaultGridOptions({
  id: 'groups-grid-detail',
  columns: columns,
  dataSource: gridDataSource,
  height: 'auto',
  paging: { enabled: false },
  editing: { allowUpdating: false, allowAdding: false, allowDeleting: false },
  searchPanel: { visible: false },
  filterRow: { visible: false },
  headerFilter: { visible: false },
  groupPanel: { visible: false },
  columnChooser: { enabled: false },
  export: { enabled: false },
  toolbar: { visible: false },
})
</script>

<template>
  <div class="q-pa-sm">
    <div v-if="componentLinks.length === 0" class="text-grey-6 q-pa-md">
      {{ $t('common.noData') }}
    </div>
    <dx-data-grid
      v-else
      v-bind="gridOptions"
    >
      <template #structure-type-cell-template="{ data: templateOptions }">
        <chip-template :values="getServiceStructureTypeChip(templateOptions.data.structure_type)" />
      </template>
    </dx-data-grid>
  </div>
</template>
